import { normalizeAssistantText } from '@/lib/assistant/assistantTextUtils';

/**
 * Normaliza modismos latinoamericanos (CL, AR, VE, CO, MX, etc.) a formas
 * que el parser de pedidos ya entiende. Se aplica antes de regex de intención.
 */
const REGIONAL_PHRASE_FIXES = [
  // Saludos / muletillas (se quitan o acortan)
  [/\b(epa|wena|wenas|que\s+onda|que\s+hubo|que\s+tal)\b[!,.\s]*/gi, ''],
  [/\b(che|po|pues|oe|wey|güey|guey|parcero|parce|chamo|pana|mi\s+rey|compadre)\b[,.\s]+/gi, ''],
  [/\b(buenas?\s+mi\s+rey|buenas?\s+compadre)\b[!,.\s]*/gi, ''],

  // Verbos de pedido → estándar
  [/\b(t[ií]rame|tirame|pasame|pásame|mandame|mándame|regalame|regálame)\b/gi, 'dame'],
  [/\b(echate|échate|echatele|échatele|metele|mételo|metele|ponle|póngale)\b/gi, 'agrega'],
  [/\b(sumale|súmale|sumaleme|agregale|agrégale)\b/gi, 'agrega'],
  [/\b(necesito\s+que\s+me\s+(mandes|traigas|pongas))\b/gi, 'quiero'],
  [/\b(me\s+anotas|anotame|anótame|apuntame|apúntame)\b/gi, 'quiero'],
  [/\b(me\s+)?gustaria\s+pedir\b/gi, 'quiero'],
  [/\b(me\s+)?gustaría\s+pedir\b/gi, 'quiero'],

  // Cantidades coloquiales
  [/\bun\s+par\s+de\b/gi, '2'],
  [/\buna\s+docena\s+de\b/gi, '12'],
  [/\bmedia\s+docena\s+de\b/gi, '6'],

  // Bebidas
  [/\b(chelas?|cheves?|birras?)\b/gi, 'cerveza'],
  [/\b(refri|refresco|soda|gaseosita|gaseositas)\b/gi, 'gaseosa'],
  [/\b(juguito|juguitos)\b/gi, 'jugo'],

  // Comida
  [/\b(hamburguesas?|hamburgesas?)\b/gi, 'hamburguesa'],
  [/\b(papitas|papas\s+fritas)\b/gi, 'papas fritas'],
  [/\b(completos?)\b/gi, 'completo'],

  // Personalización
  [/\b(sin\s+ni)\b/gi, 'sin'],
  [/\b(sácame|sacame|sacale|sácale|quitame|quítame|quitarle)\b/gi, 'sin'],
  [/\b(con\s+extra|extra\s+de)\b/gi, 'con'],

  // Envío / dirección
  [/\b(mandalo\s+a|mándalo\s+a|mandar\s+a\s+mi\s+casa)\b/gi, 'envio a'],
  [/\b(pa\s+la\s+casa|para\s+la\s+casa)\b/gi, 'a domicilio'],
  [/\b(en\s+mi\s+casa|a\s+mi\s+casa)\b/gi, 'envio a'],

  // Pago
  [/\b(pago\s+movil|pago\s+móvil|pago\s+movil)\b/gi, 'pago transferencia'],
  [/\b(transfer|transferencia\s+bancaria|deposito|depósito)\b/gi, 'transferencia'],
  [/\b(con\s+tarjeta|tarjeta\s+de\s+debito|tarjeta\s+de\s+débito|debito|débito|con\s+redcompra)\b/gi, 'pago debito'],
  [/\b(en\s+efectivo|con\s+plata|con\s+guita|con\s+lucas|con\s+billete|en\s+billete|cash)\b/gi, 'pago efectivo'],
  [/\b(mercado\s+pago|mercadopago|con\s+mp)\b/gi, 'pago mercadopago'],
];

/** Regex compartidos para pedidos en español latino. */
export const ORDER_GREETING_PREFIX_RX =
  /^(hola|hey|buenas|buenos\s+dias|buenas\s+tardes|buenas\s+noches|que\s+tal|que\s+onda|epa|wena)[,.\s!]+/i;

export const ORDER_VERB_PREFIX_RX =
  /\b(sabes\s+que\s+)?(me\s+)?(gustaria|gustaría)\s+pedir\s+|(sabes\s+que\s+)?(quiero|dame|ponme|agrego|agrega|añade|suma|sumame|sumá|necesito|anotame|apuntame|traeme|tráeme|pedi|pido)\s+/i;

export const ORDER_ADD_VERBS_RX = /\b(agrego|agrega|añade|sumame|sumá|sumale|metele|ponle|echate)\b/gi;

export const ORDER_INTENT_RX =
  /\b(quiero|dame|ponme|agrego|agrega|añade|suma|necesito|anotame|apuntame|traeme|tráeme|pido|pedi|sabes\s+que|\d+\s+\w|un\s+\w|una\s+\w)/;

export const PAYMENT_METHOD_RX =
  /\b(efectivo|transferencia|transfer|debito|débito|tarjeta|mercadopago|mercado\s+pago|plata|guita|cash|pago\s+movil)/;

export const ADDRESS_EXPLICIT_RX =
  /\b(?:direccion|dirección|domicilio|envio\s+a|envío\s+a|mandar\s+a|llevar\s+a|vivo\s+en|me\s+lo\s+mandan\s+a|la\s+direccion\s+es|la\s+dirección\s+es|manda\s+a|mándalo\s+a)\s*[:\-]?\s*(.+)$/i;

export const DRINK_SIDE_HINTS_RX =
  /\b(bebida|bebidas|gaseosa|gaseosas|jugo|jugos|coca|sprite|fanta|agua|schop|cerveza|chela|cheve|birra|refri|refresco|soda|combo\s+bebida|en\s+lata)\b/i;

export const CASH_AMOUNT_SUFFIX_RX = /\b(pesos|lucas|gamba|gambas|chirola|chirolas|billete|billetes|mango|mangos)\b/i;

export function applyRegionalOrderSpanish(text) {
  let t = String(text || '').trim();
  for (const [rx, rep] of REGIONAL_PHRASE_FIXES) {
    t = t.replace(rx, rep);
  }
  return t.replace(/\s+/g, ' ').trim();
}

export function stripOrderGreeting(text) {
  return String(text || '')
    .trim()
    .replace(ORDER_GREETING_PREFIX_RX, '')
    .trim();
}

export function hasCompositeOrderIntent(text) {
  const n = normalizeAssistantText(applyRegionalOrderSpanish(text));
  if (ORDER_INTENT_RX.test(n)) return true;
  if (/\d{1,2}\s*[xX×]\s+\w/.test(n)) return true;
  if (
    /[,;]/.test(n) &&
    /\b(hamburguesa|burger|pizza|empanada|combo|papas|bebida|gaseosa|sushi|completo|sandwich|sándwich)\b/i.test(
      n
    ) &&
    (/\b\d{1,2}\b/.test(n) || /\b(un|una|dos|tres)\b/.test(n))
  ) {
    return true;
  }
  return false;
}

export function parseRegionalCashAmount(text) {
  const raw = String(text || '');
  const lucasRx = /\b(\d{1,3})\s*(lucas?|gambas?|gamba)\b/i;
  const mLucas = raw.match(lucasRx);
  if (mLucas) {
    const n = Number(mLucas[1]);
    if (Number.isFinite(n) && n > 0) return n * 1000;
  }
  return null;
}
